import type { SystemHealthService } from '@/mocks/saSystemHealthCenter';
import { statusMeta } from '@/mocks/saSystemHealthCenter';

interface Props {
  service: SystemHealthService;
  onViewDetail: (service: SystemHealthService) => void;
  onTest: (service: SystemHealthService) => void;
  onRestart: (service: SystemHealthService) => void;
}

export default function ServiceCard({ service, onViewDetail, onTest, onRestart }: Props) {
  const meta = statusMeta[service.status];
  const hasIncidents = service.incidents.length > 0;
  const activeIncidents = service.incidents.filter((i) => !i.resolved).length;

  return (
    <div
      className={`bg-background-50 border rounded-2xl p-5 transition-all hover:border-background-300 cursor-pointer ${meta.border}`}
      onClick={() => onViewDetail(service)}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className={`w-11 h-11 rounded-xl ${meta.bg} flex items-center justify-center`}>
            <i className={`${service.icon} ${meta.color} text-lg`}></i>
          </div>
          <div>
            <p className="text-foreground-900 text-sm font-bold">{service.name}</p>
            <p className="text-foreground-400 text-[10px]">{service.version}</p>
          </div>
        </div>
        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-semibold ${meta.bg} ${meta.color}`}>
          <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`}></span>
          {meta.label}
        </span>
      </div>

      {/* Metrics grid */}
      <div className="grid grid-cols-2 gap-3 mb-4">
        <div className="bg-background-100 rounded-xl p-2.5">
          <p className="text-foreground-400 text-[9px] font-medium mb-0.5">Disponibilidad</p>
          <p className="text-foreground-900 text-xs font-bold">{service.availability.toFixed(2)}%</p>
        </div>
        <div className="bg-background-100 rounded-xl p-2.5">
          <p className="text-foreground-400 text-[9px] font-medium mb-0.5">Respuesta</p>
          <p className="text-foreground-900 text-xs font-bold">{service.responseTime}ms</p>
        </div>
        <div className="bg-background-100 rounded-xl p-2.5">
          <p className="text-foreground-400 text-[9px] font-medium mb-0.5">Última prueba</p>
          <p className="text-foreground-900 text-xs font-bold">
            {new Date(service.lastChecked).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}
          </p>
        </div>
        <div className="bg-background-100 rounded-xl p-2.5">
          <p className="text-foreground-400 text-[9px] font-medium mb-0.5">Errores recientes</p>
          <p className={`text-xs font-bold ${service.recentErrors > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
            {service.recentErrors}
          </p>
        </div>
      </div>

      {/* Incidents badge */}
      {hasIncidents && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl px-3 py-2 flex items-center justify-between">
          <span className="text-amber-700 text-[10px] font-semibold">
            {activeIncidents > 0
              ? `${activeIncidents} incidente${activeIncidents > 1 ? 's' : ''} activo${activeIncidents > 1 ? 's' : ''}`
              : `${service.incidents.length} incidente${service.incidents.length > 1 ? 's' : ''} registrado${service.incidents.length > 1 ? 's' : ''}`}
          </span>
          {activeIncidents > 0 && (
            <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse"></span>
          )}
        </div>
      )}

      {/* Action buttons */}
      <div className="flex items-center gap-2 mt-4" onClick={(e) => e.stopPropagation()}>
        <button
          onClick={() => onTest(service)}
          className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-background-100 text-foreground-600 text-[10px] font-semibold hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-flashlight-line text-xs"></i>
          Test
        </button>
        <button
          onClick={() => onRestart(service)}
          className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-background-100 text-foreground-600 text-[10px] font-semibold hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-restart-line text-xs"></i>
          Reiniciar
        </button>
      </div>
    </div>
  );
}